/* eslint-disable no-restricted-syntax, no-await-in-loop */
// Drives every chat thread for one user: restore, persist-on-change, stream, feedback.
// The hook owns the stream consumers, so a closed (but still mounted) drawer keeps
// receiving, and a run always commits into the thread that started it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChatAuthError, ChatRequestMessage, ChatVerdict, postFeedback, streamChat } from './chatStream';
import {
  ChatThread,
  conversationStore,
  emptyState,
  findThreadByPin,
  freshThread,
  pinKey,
  prune,
  StoredMessage,
  threadsByRecency,
  ThreadState,
} from './conversationStore';
import { ChatContext, contextFromLocation } from './pageContext';

export type RunState = { status: string | null; streaming: boolean; error: string | null; authExpired: boolean };

const IDLE: RunState = { status: null, streaming: false, error: null, authExpired: false };

// The server rejects consecutive same-role turns, so collapse any same-role run to
// its newest member and drop turns the user never got an answer to.
const wireHistory = (messages: StoredMessage[]): ChatRequestMessage[] => {
  const out: ChatRequestMessage[] = [];
  messages.forEach((m) => {
    if (m.interrupted || !m.text) return;
    const mapped: ChatRequestMessage = { role: m.role, text: m.text, ...(m.runId ? { runId: m.runId } : {}) };
    const prev = out[out.length - 1];
    if (prev && prev.role === m.role) out[out.length - 1] = mapped;
    else out.push(mapped);
  });
  return out;
};

// A trailing user turn means the run never came back (refresh / tab close aborts it):
// whatever arrived is kept and the turn is offered for a resend.
const markInterrupted = (thread: ChatThread): ChatThread => {
  const last = thread.messages[thread.messages.length - 1];
  if (!last || last.role !== 'user' || last.interrupted) return thread;
  return { ...thread, messages: [...thread.messages.slice(0, -1), { ...last, interrupted: true }] };
};

const restore = (userKey: string, fallback: ChatContext): ThreadState => {
  const stored = conversationStore.load(userKey);
  if (stored?.activeId) {
    const threads = Object.fromEntries(
      Object.entries(stored.threads).map(([id, thread]) => [id, markInterrupted(thread)])
    );
    return { ...stored, threads };
  }
  const first = freshThread(emptyState(), fallback);
  return { v: 3, activeId: first.id, threads: { [first.id]: first } };
};

export const useAgentChat = (userKey: string, achApiHost: string, pathname: string) => {
  const [state, setState] = useState<ThreadState>(() => restore(userKey, contextFromLocation(pathname)));
  const [runs, setRuns] = useState<Record<string, RunState>>({});

  const stateRef = useRef(state);
  const abortsRef = useRef(new Map<string, AbortController>());

  // Pruned before it is held, not only before it is written: the caps have to be true
  // of what the switcher lists, not just of what survives a reload.
  const commit = useCallback(
    (next: ThreadState) => {
      const capped = prune(next);
      stateRef.current = capped;
      setState(capped);
      conversationStore.save(userKey, capped);
    },
    [userKey]
  );

  const putThread = useCallback(
    (thread: ChatThread, activate = false) => {
      const base = stateRef.current;
      commit({
        ...base,
        activeId: activate ? thread.id : base.activeId,
        threads: { ...base.threads, [thread.id]: thread },
      });
    },
    [commit]
  );

  // Only a real unmount (sign-out, leaving the app shell) kills a run; closing the
  // drawer leaves this hook mounted.
  useEffect(() => {
    const aborts = abortsRef.current;
    return () => aborts.forEach((controller) => controller.abort());
  }, []);

  const patchRun = useCallback((threadId: string, patch: Partial<RunState>) => {
    setRuns((prev) => ({ ...prev, [threadId]: { ...(prev[threadId] ?? IDLE), ...patch } }));
  }, []);

  const run = useCallback(
    async (threadId: string) => {
      const thread = stateRef.current.threads[threadId];
      if (!thread || abortsRef.current.has(threadId)) return;

      const controller = new AbortController();
      abortsRef.current.set(threadId, controller);
      patchRun(threadId, { streaming: true, status: null, error: null, authExpired: false });

      try {
        const events = streamChat(
          achApiHost,
          {
            context: thread.context,
            conversationId: thread.conversationId,
            messages: wireHistory(thread.messages),
          },
          controller.signal
        );
        for await (const event of events) {
          if (event.kind === 'status') patchRun(threadId, { status: event.text });
          if (event.kind === 'error') patchRun(threadId, { error: event.message });
          if (event.kind === 'message') {
            const base = stateRef.current.threads[threadId];
            if (base) {
              putThread({
                ...base,
                messages: [...base.messages, { role: 'assistant', text: event.text, runId: event.runId }],
              });
            }
          }
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          if (e instanceof ChatAuthError) patchRun(threadId, { authExpired: true });
          else patchRun(threadId, { error: e instanceof Error ? e.message : 'The chat request failed.' });
        }
      } finally {
        abortsRef.current.delete(threadId);
        patchRun(threadId, { streaming: false, status: null });
      }
    },
    [achApiHost, patchRun, putThread]
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const id = stateRef.current.activeId;
      const base = id ? stateRef.current.threads[id] : null;
      if (!trimmed || !id || !base || abortsRef.current.has(id)) return;
      putThread({ ...base, draft: '', messages: [...base.messages, { role: 'user', text: trimmed }] });
      run(id);
    },
    [putThread, run]
  );

  // Covers both the mid-stream error retry and the resend of an interrupted turn —
  // the interrupted flag has to come off first or wireHistory drops the question.
  const retry = useCallback(() => {
    const id = stateRef.current.activeId;
    const base = id ? stateRef.current.threads[id] : null;
    if (!id || !base || abortsRef.current.has(id)) return;
    const last = base.messages[base.messages.length - 1];
    if (last && last.role === 'user' && last.interrupted) {
      putThread({ ...base, messages: [...base.messages.slice(0, -1), { ...last, interrupted: false }] });
    }
    run(id);
  }, [putThread, run]);

  const setDraft = useCallback(
    (text: string) => {
      const id = stateRef.current.activeId;
      const base = id ? stateRef.current.threads[id] : null;
      if (!id || !base || base.draft === text) return;
      putThread({ ...base, draft: text });
    },
    [putThread]
  );

  const selectThread = useCallback(
    (id: string) => {
      const base = stateRef.current;
      if (!base.threads[id] || base.activeId === id) return;
      commit({ ...base, activeId: id });
    },
    [commit]
  );

  // Ask-AI: the same pin reactivates its thread instead of stacking a duplicate, and
  // never touches the thread that was active.
  const startThread = useCallback(
    (context: ChatContext, draft?: string) => {
      const base = stateRef.current;
      const thread = findThreadByPin(base, context) ?? freshThread(base, context);
      putThread(draft === undefined ? thread : { ...thread, draft }, true);
    },
    [putThread]
  );

  const newChat = useCallback(() => {
    const context = contextFromLocation(pathname);
    const base = stateRef.current;
    const active = base.activeId ? base.threads[base.activeId] : null;
    // An untouched thread already pinned here IS the new chat.
    if (active && !active.messages.length && pinKey(active.context) === pinKey(context)) return;
    putThread(freshThread(base, context), true);
  }, [pathname, putThread]);

  const sendFeedback = useCallback(
    (index: number, verdict: ChatVerdict, comment?: string) => {
      const id = stateRef.current.activeId;
      const base = id ? stateRef.current.threads[id] : null;
      if (!base) return;
      const message = base.messages[index];
      if (!message || message.role !== 'assistant' || !message.runId) return;
      const next = message.feedback === verdict ? undefined : verdict;
      const messages = base.messages.slice();
      messages[index] = { ...message, feedback: next };
      putThread({ ...base, messages });
      if (next) postFeedback(achApiHost, message.runId, next, comment);
    },
    [achApiHost, putThread]
  );

  const threads = useMemo(() => threadsByRecency(state), [state]);
  const active = (state.activeId ? state.threads[state.activeId] : undefined) ?? threads[0];

  return {
    threads,
    active,
    run: runs[active.id] ?? IDLE,
    send,
    retry,
    setDraft,
    selectThread,
    startThread,
    newChat,
    sendFeedback,
  };
};

export default useAgentChat;
